import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileText,
  FolderOpen,
  Plus,
  Search,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';

import {
  createDocument,
  getDocumentDownloadUrl,
  listDocuments,
  suggestDocumentAccess,
  AiDisabledError,
  type AccessSuggestion,
  type DocumentTargetType,
  type DocumentView,
  type GrantInput,
} from '@/api/documents.api';
import type { AuthMeResponse } from '@/api/auth.api';
import type { FileAttachment } from '@/api/files.api';
import { listDepartments, listEmployees, type Department, type EmployeeListItem } from '@/api/employees.api';
import { listSeats, type SeatView } from '@/api/seats.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useToast } from '@/hooks/useToast';
import { hasCapability } from '@/lib/capabilities';
import { SYSTEM_ROLES, type SystemRole } from '@/types';

/**
 * `/documents` — the general document repository (ADR-035).
 *
 * A lighter library than the compliance SOP set: any file (policy, template,
 * handbook, form…) uploaded once, linked to one or more orgs, and shared with
 * roles / seats / departments / people. Everyone sees the documents their
 * role or seat grants; managers (`manage_documents`) upload, edit access, and
 * archive.
 *
 * Filtering (category chips + search) runs client-side over the accessible
 * set so the chips stay stable as you narrow — mirrors SopListPage.
 */
export function DocumentsPage() {
  const { state: authState } = useAuth();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canManage = me ? hasCapability(me, 'manage_documents') : false;

  const { data, isLoading, error } = useQuery({
    queryKey: ['documents'],
    queryFn: () => listDocuments(),
  });

  const [category, setCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const doc of data ?? []) set.add(doc.category);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filtered = useMemo(() => {
    const docs = data ?? [];
    const q = search.trim().toLowerCase();
    return docs.filter((doc) => {
      if (category !== 'all' && doc.category !== category) return false;
      if (q) {
        const haystack = `${doc.title} ${doc.description ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [data, category, search]);

  const downloadMutation = useMutation({
    mutationFn: async (id: string) => {
      const dl = await getDocumentDownloadUrl(id);
      window.open(dl.url, '_blank', 'noopener,noreferrer');
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not open the document.'), { variant: 'danger' });
    },
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Documents</h1>
          <p className="mt-1 max-w-2xl text-[14px] text-kw-muted">
            Policies, templates, handbooks, and forms — shared with the right people. You see the documents
            your role and seat give you access to.
          </p>
        </div>
        {canManage ? (
          <Button
            onClick={() => {
              setWizardOpen(true);
            }}
          >
            <Plus className="size-4" />
            New document
          </Button>
        ) : null}
      </div>

      {/* Filters — category chips + search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <CategoryChip
            label="All"
            active={category === 'all'}
            onClick={() => {
              setCategory('all');
            }}
          />
          {categories.map((cat) => (
            <CategoryChip
              key={cat}
              label={cat}
              active={category === cat}
              onClick={() => {
                setCategory(cat);
              }}
            />
          ))}
        </div>
        <div className="relative sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-kw-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search documents…"
            aria-label="Search documents"
            className="w-full rounded-kw-md border border-kw-border bg-white py-2 pl-8 pr-3 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load documents</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : (data ?? []).length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <FolderOpen className="size-12 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">
              {canManage ? 'No documents yet' : 'No documents shared with you yet'}
            </p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canManage
                ? 'Upload your first document to start building the library.'
                : 'When a document is shared with your role or seat, it will show up here.'}
            </p>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-[13px] text-kw-muted">
          No documents match the active filters.
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              downloading={downloadMutation.isPending && downloadMutation.variables === doc.id}
              onDownload={() => {
                downloadMutation.mutate(doc.id);
              }}
            />
          ))}
        </div>
      )}

      {canManage && me ? (
        <NewDocumentWizard
          open={wizardOpen}
          me={me}
          onClose={() => {
            setWizardOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? 'rounded-kw-full bg-kw-cerulean px-3 py-1.5 text-[12px] font-medium text-white'
          : 'rounded-kw-full border border-kw-border bg-white px-3 py-1.5 text-[12px] font-medium text-kw-shark hover:border-kw-cerulean/40 hover:text-kw-cerulean'
      }
    >
      {label}
    </button>
  );
}

function DocumentRow({
  doc,
  downloading,
  onDownload,
}: {
  doc: DocumentView;
  downloading: boolean;
  onDownload: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-kw-md border border-kw-border bg-white p-4 transition-shadow hover:shadow-kw-sm sm:flex-row sm:items-center">
      <Link to={`/documents/${doc.id}`} className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="truncate text-[15px] font-medium text-kw-shark">{doc.title}</p>
          <Badge variant="info">{doc.category}</Badge>
        </div>
        {doc.description ? (
          <p className="mt-0.5 line-clamp-1 text-[13px] text-kw-muted">{doc.description}</p>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-kw-muted">
          {doc.filename ? (
            <span className="inline-flex items-center gap-1 font-mono">
              <FileText className="size-3" />
              {doc.filename}
            </span>
          ) : null}
          <span>Uploaded by {doc.uploaderNameSnapshot}</span>
          <span>Updated {new Date(doc.updatedAt).toLocaleDateString()}</span>
        </div>
      </Link>
      <div className="shrink-0">
        <Button
          size="sm"
          variant="secondary"
          isLoading={downloading}
          disabled={!doc.fileObjectId}
          onClick={onDownload}
        >
          <Download className="size-4" />
          Download
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// New-document wizard
// ─────────────────────────────────────────────────────────────────────────

interface OrgOption {
  organizationId: string;
  name: string;
}

const TARGET_TYPE_LABELS: Record<DocumentTargetType, string> = {
  all: 'Everyone in the org',
  role: 'Role',
  seat: 'Seat',
  department: 'Department',
  membership: 'Specific person',
};

function formatRole(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** A stable key for an AI suggestion so it can be toggled + de-duped. */
function suggestionKey(s: AccessSuggestion): string {
  return `${s.targetType}:${s.targetType === 'seat' ? (s.targetSeatId ?? '') : (s.targetRole ?? '')}`;
}

function NewDocumentWizard({
  open,
  me,
  onClose,
}: {
  open: boolean;
  me: AuthMeResponse;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const currentOrgId = me.membership.organizationId;

  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 — file + metadata.
  const upload = useFileUpload({ entityType: 'document' });
  const [uploaded, setUploaded] = useState<FileAttachment | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  // Step 2 — access.
  const [suggestions, setSuggestions] = useState<AccessSuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [manualGrants, setManualGrants] = useState<GrantInput[]>([]);
  const [orgIds, setOrgIds] = useState<Set<string>>(new Set([currentOrgId]));

  const orgOptions: OrgOption[] =
    (me.memberships ?? []).length > 0
      ? (me.memberships ?? []).map((m) => ({ organizationId: m.organizationId, name: m.organization.name }))
      : [{ organizationId: currentOrgId, name: me.organization.name }];

  // Org-scoped pickers for the manual grants editor (current session org).
  const seatsQuery = useQuery({
    queryKey: ['documents', 'wizard', 'seats'],
    queryFn: () => listSeats({ scenario: 'current' }),
    enabled: open,
  });
  const departmentsQuery = useQuery({
    queryKey: ['documents', 'wizard', 'departments'],
    queryFn: listDepartments,
    enabled: open,
  });
  const employeesQuery = useQuery({
    queryKey: ['documents', 'wizard', 'employees'],
    queryFn: () => listEmployees({ status: 'active', limit: 200 }),
    enabled: open,
  });

  function resetAll(): void {
    setStep(1);
    upload.reset();
    setUploaded(null);
    setTitle('');
    setDescription('');
    setCategory('');
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setAiNotice(null);
    setManualGrants([]);
    setOrgIds(new Set([currentOrgId]));
  }

  function handleClose(): void {
    resetAll();
    onClose();
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploaded(null);
    const attachment = await upload.upload(file);
    if (attachment) setUploaded(attachment);
  }

  const suggestMutation = useMutation({
    mutationFn: () => {
      if (!uploaded) return Promise.reject(new Error('No file'));
      return suggestDocumentAccess({
        fileObjectId: uploaded.id,
        title: title.trim(),
        description: description.trim() || undefined,
        category: category.trim(),
      });
    },
    onSuccess: (result) => {
      setAiNotice(null);
      setSuggestions(result);
      if (result.length === 0) {
        setAiNotice('The assistant did not find any obvious roles or seats for this document.');
      }
    },
    onError: (err) => {
      if (err instanceof AiDisabledError) {
        setAiNotice('AI suggestions aren’t configured for this organization — add access manually below.');
        return;
      }
      const data = errData(err);
      if (data?.error === 'ai_could_not_suggest') {
        setAiNotice('The assistant couldn’t suggest access for this document — add it manually below.');
        return;
      }
      setAiNotice('The assistant is temporarily unavailable — add access manually below.');
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      if (!uploaded) return Promise.reject(new Error('No file'));
      const grants: GrantInput[] = [];
      for (const s of suggestions) {
        if (!selectedSuggestions.has(suggestionKey(s))) continue;
        grants.push({
          organizationId: currentOrgId,
          targetType: s.targetType,
          targetRole: s.targetType === 'role' ? s.targetRole : null,
          targetSeatId: s.targetType === 'seat' ? s.targetSeatId : null,
          aiSuggested: true,
        });
      }
      grants.push(...manualGrants);
      return createDocument({
        fileObjectId: uploaded.id,
        title: title.trim(),
        description: description.trim() || undefined,
        category: category.trim(),
        orgIds: Array.from(orgIds),
        grants,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.show('Document published.', { variant: 'success' });
      handleClose();
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not save the document.'), { variant: 'danger' });
    },
  });

  function toggleSuggestion(key: string): void {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleOrg(orgId: string): void {
    if (orgId === currentOrgId) return; // current org is always linked (file + grants live here)
    setOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }

  const step1Valid = Boolean(uploaded) && title.trim() !== '' && category.trim() !== '';
  const totalGrants = selectedSuggestions.size + manualGrants.length;

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} title="New document" size="lg">
      {upload.state.storageDisabled ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-kw-warning-text" />
            <div>
              <p className="text-[14px] font-medium text-kw-warning-text">File storage isn’t configured</p>
              <p className="mt-1 text-[13px] text-kw-warning-text/90">
                Documents need object storage to hold the uploaded files. Set up storage (an S3 bucket) to
                start publishing documents.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      ) : step === 1 ? (
        <Step1
          upload={upload}
          uploaded={uploaded}
          onFileChange={(e) => {
            void handleFileChange(e);
          }}
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          category={category}
          setCategory={setCategory}
          canContinue={step1Valid}
          onCancel={handleClose}
          onContinue={() => {
            setStep(2);
          }}
        />
      ) : (
        <Step2
          onBack={() => {
            setStep(1);
          }}
          onSave={() => {
            createMutation.mutate();
          }}
          saving={createMutation.isPending}
          totalGrants={totalGrants}
          // AI
          onSuggest={() => {
            suggestMutation.mutate();
          }}
          suggesting={suggestMutation.isPending}
          suggestions={suggestions}
          selectedSuggestions={selectedSuggestions}
          onToggleSuggestion={toggleSuggestion}
          aiNotice={aiNotice}
          // manual grants
          manualGrants={manualGrants}
          onAddGrant={(g) => {
            setManualGrants((prev) => [...prev, g]);
          }}
          onRemoveGrant={(idx) => {
            setManualGrants((prev) => prev.filter((_, i) => i !== idx));
          }}
          seats={seatsQuery.data ?? []}
          departments={departmentsQuery.data ?? []}
          employees={(employeesQuery.data?.data ?? []).filter((emp) => emp.membershipId)}
          currentOrgId={currentOrgId}
          // orgs
          orgOptions={orgOptions}
          orgIds={orgIds}
          onToggleOrg={toggleOrg}
        />
      )}
    </Modal>
  );
}

// ── Wizard — Step 1 (file + metadata) ─────────────────────────────────────

function Step1({
  upload,
  uploaded,
  onFileChange,
  title,
  setTitle,
  description,
  setDescription,
  category,
  setCategory,
  canContinue,
  onCancel,
  onContinue,
}: {
  upload: ReturnType<typeof useFileUpload>;
  uploaded: FileAttachment | null;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  canContinue: boolean;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const { phase, progress, error } = upload.state;
  const busy = phase === 'presigning' || phase === 'uploading' || phase === 'confirming';

  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (canContinue) onContinue();
      }}
      className="space-y-4"
    >
      <div>
        <p className="mb-1 text-[13px] font-medium text-kw-shark">File</p>
        {uploaded ? (
          <div className="flex items-center gap-3 rounded-kw-md border border-kw-tag-text/30 bg-kw-emerald-light p-3">
            <Check className="size-5 shrink-0 text-kw-tag-text" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-kw-tag-text">{uploaded.filename}</p>
              <p className="text-[11px] text-kw-tag-text/80">
                {(uploaded.sizeBytes / 1024).toFixed(0)} KB · {uploaded.contentType}
              </p>
            </div>
            <label className="cursor-pointer text-[12px] font-medium text-kw-cerulean hover:text-kw-cerulean-dark">
              Replace
              <input type="file" className="sr-only" onChange={onFileChange} />
            </label>
          </div>
        ) : (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-kw-md border border-dashed border-kw-border bg-kw-bg p-6 text-center hover:border-kw-cerulean/40">
            <Upload className="size-6 text-kw-muted" />
            <span className="text-[13px] font-medium text-kw-shark">
              {busy ? 'Uploading…' : 'Choose a file to upload'}
            </span>
            <span className="text-[11px] text-kw-muted">PDF, Word, text, or any file up to the limit.</span>
            <input type="file" className="sr-only" onChange={onFileChange} disabled={busy} />
          </label>
        )}
        {busy ? (
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-kw-full bg-kw-bg">
            <div
              className="h-full bg-kw-cerulean transition-all"
              style={{ width: `${String(Math.round(progress * 100))}%` }}
            />
          </div>
        ) : null}
        {error ? <p className="mt-1 text-[12px] text-kw-danger-text">{error}</p> : null}
      </div>

      <Input
        label="Title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        placeholder="e.g. Employee handbook"
        required
      />
      <div>
        <label htmlFor="doc-description" className="mb-1 block text-[13px] font-medium text-kw-shark">
          Description <span className="font-normal text-kw-muted">(optional)</span>
        </label>
        <textarea
          id="doc-description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          rows={3}
          placeholder="What is this document and who is it for?"
          className="w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
      </div>
      <Input
        label="Category"
        value={category}
        onChange={(e) => {
          setCategory(e.target.value);
        }}
        placeholder="e.g. HR, Policies, Templates"
        hint="A short label used to group documents in the library."
        required
      />

      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canContinue}>
          Next: access
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </form>
  );
}

// ── Wizard — Step 2 (AI suggest + grants + orgs) ──────────────────────────

interface Step2Props {
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
  totalGrants: number;
  onSuggest: () => void;
  suggesting: boolean;
  suggestions: AccessSuggestion[];
  selectedSuggestions: Set<string>;
  onToggleSuggestion: (key: string) => void;
  aiNotice: string | null;
  manualGrants: GrantInput[];
  onAddGrant: (g: GrantInput) => void;
  onRemoveGrant: (idx: number) => void;
  seats: SeatView[];
  departments: Department[];
  employees: EmployeeListItem[];
  currentOrgId: string;
  orgOptions: OrgOption[];
  orgIds: Set<string>;
  onToggleOrg: (orgId: string) => void;
}

function Step2(props: Step2Props) {
  const {
    onBack,
    onSave,
    saving,
    totalGrants,
    onSuggest,
    suggesting,
    suggestions,
    selectedSuggestions,
    onToggleSuggestion,
    aiNotice,
    manualGrants,
    onAddGrant,
    onRemoveGrant,
    seats,
    departments,
    employees,
    currentOrgId,
    orgOptions,
    orgIds,
    onToggleOrg,
  } = props;

  return (
    <div className="space-y-5">
      {/* AI suggestions */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-medium text-kw-shark">Suggested access</p>
          <Button size="sm" variant="secondary" onClick={onSuggest} isLoading={suggesting}>
            <Sparkles className="size-4" />
            Suggest with AI
          </Button>
        </div>
        {aiNotice ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3 text-[12px] text-kw-muted">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-kw-muted" />
            <span>{aiNotice}</span>
          </div>
        ) : null}
        {suggestions.length > 0 ? (
          <div className="space-y-2">
            {suggestions.map((s) => {
              const key = suggestionKey(s);
              const checked = selectedSuggestions.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onToggleSuggestion(key);
                  }}
                  aria-pressed={checked}
                  className={
                    checked
                      ? 'flex w-full items-start gap-3 rounded-kw-md border border-kw-cerulean bg-kw-cerulean-light/60 p-3 text-left'
                      : 'flex w-full items-start gap-3 rounded-kw-md border border-kw-border bg-white p-3 text-left hover:border-kw-cerulean/40'
                  }
                >
                  <span
                    className={
                      checked
                        ? 'mt-0.5 grid size-4 shrink-0 place-items-center rounded border border-kw-cerulean bg-kw-cerulean text-white'
                        : 'mt-0.5 size-4 shrink-0 rounded border border-kw-border'
                    }
                  >
                    {checked ? <Check className="size-3" /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-kw-shark">{s.targetLabel}</span>
                    <span className="block text-[12px] text-kw-muted">{s.reason}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </section>

      {/* Manual grants editor */}
      <GrantsEditor
        grants={manualGrants}
        onAdd={onAddGrant}
        onRemove={onRemoveGrant}
        seats={seats}
        departments={departments}
        employees={employees}
        currentOrgId={currentOrgId}
      />

      {/* Org selector */}
      <section className="space-y-2">
        <p className="text-[13px] font-medium text-kw-shark">Share with organizations</p>
        <div className="space-y-1.5">
          {orgOptions.map((org) => {
            const isCurrent = org.organizationId === currentOrgId;
            return (
              <label key={org.organizationId} className="flex items-center gap-2 text-[13px] text-kw-shark">
                <input
                  type="checkbox"
                  checked={orgIds.has(org.organizationId)}
                  disabled={isCurrent}
                  onChange={() => {
                    onToggleOrg(org.organizationId);
                  }}
                  className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/30"
                />
                {org.name}
                {isCurrent ? <span className="text-[11px] text-kw-muted">(current)</span> : null}
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex items-center justify-between gap-2 border-t border-kw-border pt-4">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-kw-muted">
            {totalGrants === 0
              ? 'No access grants yet'
              : `${String(totalGrants)} access grant${totalGrants === 1 ? '' : 's'}`}
          </span>
          <Button onClick={onSave} isLoading={saving}>
            Publish document
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Wizard — the manual grants editor ─────────────────────────────────────

export function GrantsEditor({
  grants,
  onAdd,
  onRemove,
  seats,
  departments,
  employees,
  currentOrgId,
}: {
  grants: GrantInput[];
  onAdd: (g: GrantInput) => void;
  onRemove: (idx: number) => void;
  seats: SeatView[];
  departments: Department[];
  employees: EmployeeListItem[];
  currentOrgId: string;
}) {
  const [targetType, setTargetType] = useState<DocumentTargetType>('role');
  const [role, setRole] = useState<SystemRole>('employee');
  const [seatId, setSeatId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [membershipId, setMembershipId] = useState('');

  function add(): void {
    const base: GrantInput = { organizationId: currentOrgId, targetType, aiSuggested: false };
    if (targetType === 'role') onAdd({ ...base, targetRole: role });
    else if (targetType === 'seat') {
      if (!seatId) return;
      onAdd({ ...base, targetSeatId: seatId });
    } else if (targetType === 'department') {
      if (!departmentId) return;
      onAdd({ ...base, targetDepartmentId: departmentId });
    } else if (targetType === 'membership') {
      if (!membershipId) return;
      onAdd({ ...base, targetMembershipId: membershipId });
    } else {
      onAdd(base); // 'all'
    }
  }

  return (
    <section className="space-y-2">
      <p className="text-[13px] font-medium text-kw-shark">Add access</p>
      <div className="flex flex-wrap items-end gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
        <SelectField
          label="Grant to"
          value={targetType}
          onChange={(v) => {
            setTargetType(v as DocumentTargetType);
          }}
          options={(Object.keys(TARGET_TYPE_LABELS) as DocumentTargetType[]).map((t) => ({
            value: t,
            label: TARGET_TYPE_LABELS[t],
          }))}
        />
        {targetType === 'role' ? (
          <SelectField
            label="Role"
            value={role}
            onChange={(v) => {
              setRole(v as SystemRole);
            }}
            options={SYSTEM_ROLES.map((r) => ({ value: r, label: formatRole(r) }))}
          />
        ) : null}
        {targetType === 'seat' ? (
          <SelectField
            label="Seat"
            value={seatId}
            onChange={setSeatId}
            options={[
              { value: '', label: 'Select a seat…' },
              ...seats.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        ) : null}
        {targetType === 'department' ? (
          <SelectField
            label="Department"
            value={departmentId}
            onChange={setDepartmentId}
            options={[
              { value: '', label: 'Select a department…' },
              ...departments.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        ) : null}
        {targetType === 'membership' ? (
          <SelectField
            label="Person"
            value={membershipId}
            onChange={setMembershipId}
            options={[
              { value: '', label: 'Select a person…' },
              ...employees
                .filter((e) => e.membershipId)
                .map((e) => ({
                  value: e.membershipId ?? '',
                  label: `${e.firstName} ${e.lastName}`,
                })),
            ]}
          />
        ) : null}
        <Button type="button" size="sm" variant="secondary" onClick={add}>
          <Plus className="size-4" />
          Add
        </Button>
      </div>

      {grants.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {grants.map((g, idx) => (
            <li
              key={`${g.targetType}-${String(idx)}`}
              className="inline-flex items-center gap-1.5 rounded-kw-full bg-kw-cerulean-light px-2.5 py-1 text-[12px] text-kw-cerulean-dark"
            >
              {describeGrant(g, seats, departments, employees)}
              <button
                type="button"
                aria-label="Remove grant"
                onClick={() => {
                  onRemove(idx);
                }}
                className="text-kw-cerulean-dark/70 hover:text-kw-danger-text"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function describeGrant(
  g: GrantInput,
  seats: SeatView[],
  departments: Department[],
  employees: EmployeeListItem[],
): string {
  switch (g.targetType) {
    case 'all':
      return 'Everyone';
    case 'role':
      return `Role: ${formatRole(g.targetRole ?? '')}`;
    case 'seat':
      return `Seat: ${seats.find((s) => s.id === g.targetSeatId)?.name ?? g.targetSeatId ?? ''}`;
    case 'department':
      return `Dept: ${departments.find((d) => d.id === g.targetDepartmentId)?.name ?? g.targetDepartmentId ?? ''}`;
    case 'membership': {
      const emp = employees.find((e) => e.membershipId === g.targetMembershipId);
      return `Person: ${emp ? `${emp.firstName} ${emp.lastName}` : (g.targetMembershipId ?? '')}`;
    }
    default:
      return 'Access';
  }
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const id = `grant-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium text-kw-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────

/** Read the `{ error }` envelope off an axios error, if present. */
function errData(err: unknown): { error?: string; message?: string } | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  }
  return undefined;
}

function describeDocError(err: unknown, fallback: string): string {
  const data = errData(err);
  switch (data?.error) {
    case 'storage_disabled':
      return 'File storage isn’t configured, so the document can’t be served.';
    case 'document_not_found':
      return 'That document is no longer available.';
    case 'file_not_found':
      return 'The attached file could not be found.';
    case 'org_not_permitted':
      return 'You can only share documents with organizations you belong to.';
    case 'invalid_file':
      return 'The uploaded file is not ready — try uploading it again.';
    case 'invalid_grant':
      return 'One of the access grants points at something that no longer exists.';
    case 'validation_failed':
      return 'Some fields are missing or invalid.';
    default:
      if (typeof data?.message === 'string') return data.message;
      return err instanceof Error ? err.message : fallback;
  }
}
